import type { PolicyFeature } from '@/services/policy-features'

/** Infrastructure features that must stay enabled for tenant/org/root-org scopes. */
export const INSTANCE_FEATURE_ASSIGNMENT_LOCKED = new Set([
  'Features',
  'In-Apps',
  'MobileApps',
])

export const isAssignmentLockedFeature = (
  feature: Pick<PolicyFeature, 'feature' | 'assignmentLocked'>,
) => {
  if (feature.assignmentLocked) {
    return true
  }

  return INSTANCE_FEATURE_ASSIGNMENT_LOCKED.has(String(feature.feature ?? '').trim())
}

export const collectLockedFeatureIds = (features: PolicyFeature[]) => (
  features.filter(isAssignmentLockedFeature).map((feature) => feature.id)
)

export const mergeEnabledFeatureIds = (
  features: PolicyFeature[],
  selectedIds: number[],
) => Array.from(new Set([
  ...selectedIds,
  ...collectLockedFeatureIds(features),
]))

export const buildInstanceFeatureOverrideUpdates = ({
  features,
  selectedIds,
  originalSelectedIds,
}: {
  features: PolicyFeature[]
  selectedIds: number[]
  /**
   * Selection state captured when the modal was opened. When provided, only
   * features whose resolved branchHidden value actually differs from this
   * baseline are included, so Save doesn't re-submit unchanged features.
   */
  originalSelectedIds?: number[]
}) => {
  const selectedFeatureIds = new Set(mergeEnabledFeatureIds(features, selectedIds))
  const originalFeatureIds = originalSelectedIds
    ? new Set(mergeEnabledFeatureIds(features, originalSelectedIds))
    : null

  return features
    .filter((feature) => !isAssignmentLockedFeature(feature))
    .map((feature) => ({
      id: feature.id,
      branchHidden: !selectedFeatureIds.has(feature.id),
      originallyHidden: originalFeatureIds ? !originalFeatureIds.has(feature.id) : null,
    }))
    .filter((update) => update.originallyHidden === null || update.originallyHidden !== update.branchHidden)
    .map(({ id, branchHidden }) => ({ id, branchHidden }))
}
